import { type CSSVariablesResolver, type MantineColorsTuple, type MantineThemeOverride, createTheme } from '@mantine/core';
import { CI_THEME, type ThemeColors, mix, shades } from '@pst/shared';

/**
 * Visual language of PST.
 *
 * A planning tool is read far more often than it is operated, sometimes for an
 * hour at a stretch. So the aim is not decoration but legibility: one restrained
 * accent, quiet greys, and colour reserved for things that actually mean
 * something - critical path, negative float, a phase the user coloured on
 * purpose. A colourful interface would leave no colour left to say anything.
 */

/** For the critical path. Serious, not an alarm - and not a colour of the office's palette. */
const critical: MantineColorsTuple = [
  '#fdeceb',
  '#f7d7d5',
  '#eeaca8',
  '#e57e78',
  '#de5850',
  '#da4036',
  '#d9332a',
  '#c1261e',
  '#ac1f19',
  '#961512',
];

const tuple = (list: string[]): MantineColorsTuple => list as unknown as MantineColorsTuple;

/** Archivo, the office's typeface (CI V01), served from this origin; system fonts until it has loaded. */
const FONT = "'Archivo', 'Arial', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/** The colours in force, for what is drawn outside React - the Excel files. */
let current: ThemeColors = CI_THEME;
export const currentColors = (): ThemeColors => current;

/**
 * The program's look from the office's colours (Einstellungen › Darstellung;
 * default: the corporate design). The main colour becomes a scale of ten
 * shades like the CI's "Pflaume 10 … 80", greys are warmed towards the text
 * colour as the CI's neutrals are, and the dark mode is derived from the
 * same colours.
 */
export function makeTheme(colors: ThemeColors = CI_THEME): { theme: MantineThemeOverride; resolver: CSSVariablesResolver } {
  current = colors;
  const white = '#FFFFFF';
  const black = '#000000';
  const t = colors.text;
  // The lightest grey must stand out from the paper tone (#F6F4F1), so it starts a step darker than white-ish.
  const gray = tuple([mix(t, white, 0.93), mix(t, white, 0.89), mix(t, white, 0.84), mix(t, white, 0.78), mix(t, white, 0.62), mix(t, white, 0.48), mix(t, white, 0.3), mix(t, white, 0.18), mix(t, white, 0.08), t]);
  const dark = tuple([mix(t, white, 0.78), mix(t, white, 0.62), mix(t, white, 0.45), mix(t, white, 0.28), mix(t, white, 0.14), mix(t, white, 0.04), mix(t, black, 0.15), mix(t, black, 0.32), mix(t, black, 0.48), mix(t, black, 0.6)]);
  const theme = createTheme({
    primaryColor: 'brand',
    // On dark ground a lighter shade for filled areas (white text stays readable, 5.7 : 1 for
    // Pflaume); links and coloured text use a lighter one still (--mantine-color-anchor below),
    // as the CI rules out Pflaume on Anthrazit (1.7 : 1).
    primaryShade: { light: 6, dark: 5 },
    colors: { brand: tuple(shades(colors.primary)), accent: tuple(shades(colors.accent)), critical, gray, dark },
    black: t,
    fontFamily: FONT,
    fontFamilyMonospace: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, monospace",
    headings: {
      fontFamily: FONT,
      fontWeight: '600',
      sizes: {
        h3: { fontSize: '1.25rem', lineHeight: '1.3' },
        h4: { fontSize: '1.05rem', lineHeight: '1.3' },
        h5: { fontSize: '0.95rem', lineHeight: '1.35' },
      },
    },
    fontSizes: { xs: '0.72rem', sm: '0.82rem', md: '0.9rem', lg: '1rem', xl: '1.15rem' },
    defaultRadius: 'md',
    cursorType: 'pointer',
    components: {
      Card: { defaultProps: { shadow: 'none' } },
      Button: { defaultProps: { fw: 500 } },
      Badge: { defaultProps: { fw: 500, tt: 'none' } },
    },
  });
  const resolver: CSSVariablesResolver = () => ({
    variables: {},
    light: {
      '--mantine-color-text': t,
      '--mantine-color-dimmed': mix(t, white, 0.3),
      '--pst-page': colors.background,
      '--pst-accent': colors.accent,
      '--pst-mark-ground': mix(colors.primary, black, 0.35),
      '--pst-mark-bar': mix(colors.primary, white, 0.9),
    },
    dark: {
      '--mantine-color-anchor': mix(colors.primary, white, 0.55),
      '--mantine-color-brand-text': mix(colors.primary, white, 0.55),
      '--pst-page': mix(t, black, 0.32),
      '--pst-accent': mix(colors.accent, white, 0.15),
      '--pst-mark-ground': mix(colors.primary, black, 0.5),
      '--pst-mark-bar': mix(colors.primary, white, 0.85),
    },
  });
  return { theme, resolver };
}

/**
 * Colours the chart uses, as CSS variables so both colour schemes work
 * without a second palette in JavaScript.
 */
/**
 * Status strip under a bar.
 *
 * Criticality and float used to fight the bar colour for the same channel. They
 * now sit on their own: a thin strip under the bar, in colours chosen to sit
 * well beside the presets of the colour picker rather than to shout over them.
 */
export const status = {
  /** No float at all - on the critical path. */
  critical: '#d9332a',
  /** Negative float - the imposed finish cannot be met. */
  behind: '#e8590c',
  /** Float to spare. Present, but not competing with the bar. */
  slack: '#2f9e44',
  slackOpacity: 0.55,
} as const;

export const chart = {
  bar: 'var(--mantine-color-brand-6)',
  barCritical: 'var(--mantine-color-critical-6)',
  phase: 'light-dark(var(--mantine-color-dark-4), var(--mantine-color-dark-1))',
  grid: 'var(--mantine-color-default-border)',
  gridStrong: 'light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-3))',
  weekend: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
  today: 'var(--pst-accent)',
  link: 'light-dark(var(--mantine-color-gray-6), var(--mantine-color-dark-2))',
  /** The frozen plan: present, quiet, clearly not the current bar. */
  baseline: 'light-dark(var(--mantine-color-gray-5), var(--mantine-color-dark-2))',
  text: 'var(--mantine-color-text)',
  dimmed: 'var(--mantine-color-dimmed)',
} as const;
